import {
  formatIsoDateTime,
  isoToDisplayDate,
  isoToDisplayTime,
  parseIsoDateTime,
} from "./isoDate";

export type DateTimeRange = {
  from: string;
  to: string;
};

export type DateRangePresetId =
  | "today"
  | "yesterday"
  | "this_week"
  | "this_month"
  | "this_year"
  | "from_start_of_day"
  | "from_start_of_week"
  | "last_7_days"
  | "last_30_days"
  | "prev_week"
  | "prev_month"
  | "all";

export type DateRangePreset = {
  id: DateRangePresetId;
  label: string;
};

export const DATE_RANGE_PRESETS_MAIN: DateRangePreset[] = [
  { id: "today", label: "Сегодня" },
  { id: "yesterday", label: "Вчера" },
  { id: "this_week", label: "Эта неделя" },
  { id: "this_month", label: "Этот месяц" },
  { id: "this_year", label: "Этот год" },
  { id: "from_start_of_day", label: "С начала дня" },
  { id: "from_start_of_week", label: "С начала недели" },
];

export const DATE_RANGE_PRESETS_OTHER: DateRangePreset[] = [
  { id: "last_7_days", label: "Последние 7 дней" },
  { id: "last_30_days", label: "Последние 30 дней" },
  { id: "prev_week", label: "Прошлая неделя" },
  { id: "prev_month", label: "Прошлый месяц" },
  { id: "all", label: "За все время" },
];

function startOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0);
}

function endOfDay(date: Date): Date {
  return new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    23,
    59,
    59,
  );
}

function startOfWeekMonday(date: Date): Date {
  const weekday = date.getDay();
  const diff = weekday === 0 ? 6 : weekday - 1;
  return startOfDay(
    new Date(date.getFullYear(), date.getMonth(), date.getDate() - diff),
  );
}

function range(from: Date, to: Date): DateTimeRange {
  return { from: formatIsoDateTime(from), to: formatIsoDateTime(to) };
}

export function resolveDateRangePreset(
  id: DateRangePresetId,
  now = new Date(),
): DateTimeRange {
  if (id === "all") return { from: "", to: "" };

  const today = startOfDay(now);
  const weekStart = startOfWeekMonday(now);

  switch (id) {
    case "today":
      return range(today, endOfDay(today));
    case "yesterday": {
      const yesterday = startOfDay(
        new Date(today.getFullYear(), today.getMonth(), today.getDate() - 1),
      );
      return range(yesterday, endOfDay(yesterday));
    }
    case "this_week": {
      const weekEnd = endOfDay(
        new Date(
          weekStart.getFullYear(),
          weekStart.getMonth(),
          weekStart.getDate() + 6,
        ),
      );
      return range(weekStart, weekEnd);
    }
    case "this_month": {
      const monthStart = new Date(
        now.getFullYear(),
        now.getMonth(),
        1,
        0,
        0,
        0,
      );
      const monthEnd = endOfDay(
        new Date(now.getFullYear(), now.getMonth() + 1, 0),
      );
      return range(monthStart, monthEnd);
    }
    case "this_year": {
      const yearStart = new Date(now.getFullYear(), 0, 1, 0, 0, 0);
      const yearEnd = endOfDay(new Date(now.getFullYear(), 11, 31));
      return range(yearStart, yearEnd);
    }
    case "from_start_of_day":
      return range(today, now);
    case "from_start_of_week":
      return range(weekStart, now);
    case "last_7_days": {
      const from = startOfDay(
        new Date(today.getFullYear(), today.getMonth(), today.getDate() - 6),
      );
      return range(from, endOfDay(today));
    }
    case "last_30_days": {
      const from = startOfDay(
        new Date(today.getFullYear(), today.getMonth(), today.getDate() - 29),
      );
      return range(from, endOfDay(today));
    }
    case "prev_week": {
      const from = startOfDay(
        new Date(
          weekStart.getFullYear(),
          weekStart.getMonth(),
          weekStart.getDate() - 7,
        ),
      );
      const to = endOfDay(
        new Date(from.getFullYear(), from.getMonth(), from.getDate() + 6),
      );
      return range(from, to);
    }
    case "prev_month": {
      const from = new Date(now.getFullYear(), now.getMonth() - 1, 1, 0, 0, 0);
      const to = endOfDay(new Date(now.getFullYear(), now.getMonth(), 0));
      return range(from, to);
    }
  }
}

export function formatDateRangeTriggerLabel(
  from: string,
  to: string,
  presetLabel?: string,
): string {
  if (!from && !to) return "За все время";
  if (presetLabel) return presetLabel;

  const fromDate = parseIsoDateTime(from);
  const toDate = parseIsoDateTime(to);
  const fromText = fromDate
    ? `${isoToDisplayDate(from)}${isoToDisplayTime(from) !== "00:00" ? ` ${isoToDisplayTime(from)}` : ""}`
    : "";
  const toText = toDate
    ? `${isoToDisplayDate(to)}${isoToDisplayTime(to) !== "23:59" && isoToDisplayTime(to) !== "00:00" ? ` ${isoToDisplayTime(to)}` : ""}`
    : "";

  if (fromText && toText) return `${fromText} — ${toText}`;
  if (fromText) return `с ${fromText}`;
  if (toText) return `до ${toText}`;
  return "За все время";
}

export function isTimestampInRange(
  value: string | undefined,
  from: string,
  to: string,
): boolean {
  if (!from && !to) return true;
  if (!value) return false;
  const time = new Date(value).getTime();
  if (Number.isNaN(time)) return false;

  if (from) {
    const fromDate = parseIsoDateTime(from);
    if (fromDate && time < fromDate.getTime()) return false;
  }
  if (to) {
    const toDate = parseIsoDateTime(to);
    if (toDate && time > toDate.getTime()) return false;
  }
  return true;
}

export function filterItemsByCreatedAt<T>(
  items: T[],
  from: string,
  to: string,
  getCreatedAt: (item: T) => string | undefined,
): T[] {
  if (!from && !to) return items;
  return items.filter((item) =>
    isTimestampInRange(getCreatedAt(item), from, to),
  );
}
